/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { JournalVoucher10Info } from "./info";
import { JournalVoucher10Service } from "services/journal-voucher-10";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const emptyRow = () => ({
  index: 0,
  description: "",
  openingDebit: 0,
  openingCredit: 0,
  debitValues: [0, 0, 0, 0],
  totalDebit: 0,
  creditValues: [0, 0, 0, 0],
  totalCredit: 0,
  closingDebit: 0,
  closingCredit: 0,
});

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  debitAccountLabels: ["", "", "", ""],
  creditAccountLabels: ["", "", "", ""],
  rows: [emptyRow()],
  totals: {
    openingDebit: 0,
    openingCredit: 0,
    debitValues: [0, 0, 0, 0],
    totalDebit: 0,
    creditValues: [0, 0, 0, 0],
    totalCredit: 0,
    closingDebit: 0,
    closingCredit: 0,
  },
  recordedToLedgerDay: "",
  recordedToLedgerMonth: "",
  recordedToLedgerYear: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function JournalVoucher10Detail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await JournalVoucher10Service.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/journal-voucher-10");

  const num = (v) => (v !== undefined && v !== "" && !isNaN(v) ? Number(v) : 0);

  const submitForm = async (fValues) => {
    try {
      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        month: fValues.month,
        year: fValues.year,
        debitAccountLabels: fValues.debitAccountLabels,
        creditAccountLabels: fValues.creditAccountLabels,
        rows: (fValues.rows || []).map((r, idx) => ({
          ...r,
          index: r.index || idx + 1,
          openingDebit: num(r.openingDebit),
          openingCredit: num(r.openingCredit),
          debitValues: (r.debitValues || []).map(num),
          totalDebit: num(r.totalDebit),
          creditValues: (r.creditValues || []).map(num),
          totalCredit: num(r.totalCredit),
          closingDebit: num(r.closingDebit),
          closingCredit: num(r.closingCredit),
        })),
        totals: {
          openingDebit: num(fValues.totals.openingDebit),
          openingCredit: num(fValues.totals.openingCredit),
          debitValues: (fValues.totals.debitValues || []).map(num),
          totalDebit: num(fValues.totals.totalDebit),
          creditValues: (fValues.totals.creditValues || []).map(num),
          totalCredit: num(fValues.totals.totalCredit),
          closingDebit: num(fValues.totals.closingDebit),
          closingCredit: num(fValues.totals.closingCredit),
        },
        recordedToLedgerDay: fValues.recordedToLedgerDay,
        recordedToLedgerMonth: fValues.recordedToLedgerMonth,
        recordedToLedgerYear: fValues.recordedToLedgerYear,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await JournalVoucher10Service.create(payload);
        toast.success("Tạo nhật ký chứng từ thành công!");
      } else {
        await JournalVoucher10Service.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/journal-voucher-10");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S04a10-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <JournalVoucher10Info formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Nhật ký S04a10-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher10Detail;
